import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  MapPin,
  List,
  Map as MapIcon,
  LocateFixed,
  RefreshCcw,
  Utensils,
  Crown,
  Share2,
  Plus,
} from "lucide-react";
import { toast } from "sonner";

import MapView from "@/components/MapView";
import RestaurantCard from "@/components/RestaurantCard";
import ClaimSponsorDialog from "@/components/ClaimSponsorDialog";
import RestaurantDetailsSheet from "@/components/RestaurantDetailsSheet";
import ReportDialog from "@/components/ReportDialog";
import AddRestaurantDialog from "@/components/AddRestaurantDialog";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import FeaturedNearby from "@/components/FeaturedNearby";
import { fetchNearby, geocodeAddress, resolvePlace } from "@/lib/api";
import { CUISINES } from "@/lib/cuisines";
import { readUrlState, writeUrlState } from "@/lib/urlState";

// Reasonable default center if geolocation fails entirely
const FALLBACK_CENTER = {
  lat: -37.8136,
  lng: 144.9631,
  label: "Melbourne, Australia",
};

export default function HalalFinder() {
  // Lazily-evaluated URL state (read once on mount)
  const initialUrl = useMemo(() => readUrlState(), []);

  const [center, setCenter] = useState(
    initialUrl.lat != null && initialUrl.lng != null
      ? { lat: initialUrl.lat, lng: initialUrl.lng }
      : null
  );
  const [zoom, setZoom] = useState(initialUrl.zoom || 13);
  const [centerLabel, setCenterLabel] = useState(
    initialUrl.lat != null ? "Shared location" : ""
  );
  const [radius, setRadius] = useState(3000);
  const [cuisine, setCuisine] = useState(initialUrl.cuisine || "all");
  const [openNowOnly, setOpenNowOnly] = useState(!!initialUrl.openNow);
  const [restaurants, setRestaurants] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [mobileView, setMobileView] = useState("map"); // 'map' | 'list'
  const [geoBlocked, setGeoBlocked] = useState(false);
  const [claimRestaurant, setClaimRestaurant] = useState(null);
  const [detailsRestaurant, setDetailsRestaurant] = useState(null);
  const [reportRestaurant, setReportRestaurant] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  // True device geolocation (separate from `center` which moves with panning).
  // Used to bias autocomplete suggestions toward what's closest to the user.
  const [userLocation, setUserLocation] = useState(null);
  // Increments whenever user explicitly recenters (geolocation / city search / refresh).
  // Used by MapView to do a one-shot fitBounds without fighting later panning.
  const [fitKey, setFitKey] = useState(0);

  const cardRefs = useRef({});
  const pendingDetailsPlaceId = useRef(initialUrl.place_id || null);

  // ----------- Geolocation -----------
  const requestGeolocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoBlocked(true);
      setCenter(FALLBACK_CENTER);
      setCenterLabel(FALLBACK_CENTER.label);
      toast.error("Geolocation not supported by your browser.");
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const me = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCenter(me);
        setUserLocation(me);
        setCenterLabel("My location");
        setGeoBlocked(false);
        setFitKey((k) => k + 1);
      },
      (err) => {
        console.warn("Geolocation error:", err);
        setGeoBlocked(true);
        setCenter(FALLBACK_CENTER);
        setCenterLabel(FALLBACK_CENTER.label);
        setFitKey((k) => k + 1);
        toast.message("Location permission denied", {
          description: "Showing Melbourne as default. Search a city to change.",
        });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, []);

  useEffect(() => {
    // Don't auto-geolocate if the URL specified a center (shared link).
    if (initialUrl.lat != null && initialUrl.lng != null) return;
    requestGeolocation();
  }, [requestGeolocation, initialUrl.lat, initialUrl.lng]);

  // ----------- Fetch restaurants when center / radius / cuisine changes -----------
  const loadNearby = useCallback(async () => {
    if (!center) return;
    setLoading(true);
    try {
      const data = await fetchNearby({
        lat: center.lat,
        lng: center.lng,
        radius,
        cuisine,
      });
      setRestaurants(data.results || []);
      if ((data.results || []).length === 0) {
        toast.message("No halal places in this area", {
          description: "Try a different cuisine or pan the map.",
        });
      }
    } catch (e) {
      console.error(e);
      // Extract a genuinely useful description. Cloudflare's HTML error
      // pages don't have a JSON `.detail`, and the raw body has scary copy
      // like "The origin web server returned an invalid response…" which
      // isn't actionable for the diner. Show a friendly retryable message
      // instead when we detect an edge/gateway error.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const isDetailString = typeof detail === "string" && detail.length < 300;
      const isEdge5xx =
        [502, 503, 504, 520, 521, 522, 523, 524].includes(status) ||
        e?.code === "ERR_NETWORK";
      const description = isEdge5xx
        ? "Our servers hiccuped for a moment. Tap Refresh in a few seconds — usually recovers on its own."
        : isDetailString
        ? detail
        : "Please try refreshing the map or panning to a different area.";
      toast.error("Failed to load restaurants", { description });
    } finally {
      setLoading(false);
    }
  }, [center, radius, cuisine]);

  useEffect(() => {
    loadNearby();
  }, [loadNearby]);

  // Map reports new bounds when user pans/zooms (debounced in MapView).
  const handleBoundsChange = useCallback(({ lat, lng, radius: r, zoom: z }) => {
    setCenter({ lat, lng });
    setRadius(r);
    if (z != null) setZoom(z);
    setCenterLabel("This area");
  }, []);

  // Sync URL whenever center / cuisine / zoom / details / open-now change.
  useEffect(() => {
    if (!center) return;
    writeUrlState({
      cuisine,
      lat: center.lat,
      lng: center.lng,
      zoom,
      place_id: detailsRestaurant?.place_id,
      openNow: openNowOnly,
    });
  }, [cuisine, center, zoom, detailsRestaurant?.place_id, openNowOnly]);

  // If URL had ?p=placeId, open the details sheet once the restaurant appears.
  useEffect(() => {
    const targetId = pendingDetailsPlaceId.current;
    if (!targetId || restaurants.length === 0) return;
    const found = restaurants.find((r) => r.place_id === targetId);
    if (found) {
      setDetailsRestaurant(found);
      setActiveId(found.place_id);
      pendingDetailsPlaceId.current = null;
    }
  }, [restaurants]);

  // ----------- Smart search: detects cuisine + optional location -----------
  // Examples:
  //   "biryani"            -> cuisine=biryani
  //   "shawarma melbourne" -> cuisine=shawarma + geocode("melbourne")
  //   "tokyo"              -> geocode("tokyo")
  //   "halal indian london"-> cuisine=indian + geocode("halal london") -> cleaned to "london"
  const detectCuisineFromQuery = (q) => {
    const lower = q.toLowerCase().trim();
    // Strip generic words that don't help
    const cleaned = lower.replace(/\b(halal|restaurant|restaurants|food|near|me)\b/g, "").trim();
    // Try direct id match (e.g. "shawarma" or "middle_eastern")
    const direct = CUISINES.find(
      (c) =>
        c.id !== "all" &&
        (c.id === cleaned || c.label.toLowerCase() === cleaned)
    );
    if (direct) return { cuisine: direct, remaining: "" };
    // Try token match — find a cuisine label/id present anywhere in the query
    const tokens = cleaned.split(/\s+/).filter(Boolean);
    for (const c of CUISINES) {
      if (c.id === "all") continue;
      const label = c.label.toLowerCase();
      // multi-word label match (e.g. "middle eastern")
      if (label.includes(" ") && cleaned.includes(label)) {
        return {
          cuisine: c,
          remaining: cleaned.replace(label, "").replace(/\s+/g, " ").trim(),
        };
      }
      if (tokens.includes(c.id) || tokens.includes(label)) {
        return {
          cuisine: c,
          remaining: tokens.filter((t) => t !== c.id && t !== label).join(" "),
        };
      }
    }
    return { cuisine: null, remaining: q };
  };

  const handleSearch = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const q = (searchInput || "").trim();
    if (!q) return;

    const { cuisine: matched, remaining } = detectCuisineFromQuery(q);
    if (matched) {
      setCuisine(matched.id);
      setSearchInput("");
      toast.success(`Showing ${matched.label} places`, {
        description: remaining ? `in ${remaining}` : "in this area",
      });
      if (!remaining) return; // no location part — keep current map area
      // else fall through to geocode `remaining`
    }

    const geocodeQuery = matched ? remaining : q;
    if (!geocodeQuery) return;
    setLoading(true);
    try {
      const g = await geocodeAddress(geocodeQuery);
      setCenter({ lat: g.lat, lng: g.lng });
      setCenterLabel(g.formatted_address);
      setGeoBlocked(false);
      setFitKey((k) => k + 1);
      if (!matched) setSearchInput("");
    } catch (err) {
      toast.error("Couldn't find that place", {
        description: err?.response?.data?.detail || err.message,
      });
      setLoading(false);
    }
  };

  // Autocomplete pick: jump to the picked place's coordinates (resolves via
  // backend → places/resolve). If it's a restaurant, also open its details.
  const handlePickSuggestion = async (s) => {
    if (!s) return;
    setSearchInput("");
    if (!s.place_id) {
      // Fallback: treat as free-text city search
      setSearchInput(s.description || "");
      return;
    }
    setLoading(true);
    try {
      const place = await resolvePlace(s.place_id);
      setCenter({ lat: place.lat, lng: place.lng });
      setCenterLabel(place.address || place.name || s.description);
      setGeoBlocked(false);
      setFitKey((k) => k + 1);
      // If the picked place is itself a restaurant, queue it for details
      const isRestaurant = (place.types || []).some((t) =>
        ["restaurant", "cafe", "bakery", "meal_takeaway", "food"].includes(t)
      );
      if (isRestaurant) {
        pendingDetailsPlaceId.current = place.place_id;
      }
    } catch (err) {
      toast.error("Couldn't open that place", {
        description: err?.response?.data?.detail || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // ----------- Marker / card interaction -----------
  const handleMarkerClick = (placeId) => {
    setActiveId(placeId);
    // Scroll list to the active card (desktop sidebar view)
    const ref = cardRefs.current[placeId];
    if (ref && typeof ref.scrollIntoView === "function") {
      ref.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
    // Also open the details sheet (works on both desktop & mobile)
    const r = restaurants.find((x) => x.place_id === placeId);
    if (r) setDetailsRestaurant(r);
  };

  const handleCardClick = (placeId) => {
    setActiveId(placeId);
    const r = restaurants.find((x) => x.place_id === placeId);
    if (r) setDetailsRestaurant(r);
  };

  const handleShare = useCallback(async () => {
    const url = window.location.href;
    const title = "AlKoncierge — nearby halal restaurants";
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success("Link copied!", {
        description: "Anyone who opens it sees this exact view.",
      });
    } catch (err) {
      // AbortError = user dismissed the native share sheet; that's expected
      // and shouldn't surface as a toast. Anything else we log for debugging.
      if (err?.name !== "AbortError") {
        console.warn("[share] failed:", err?.message || err);
      }
    }
  }, []);

  const displayedRestaurants = useMemo(() => {
    if (!openNowOnly) return restaurants;
    // open_now=null means we don't know — exclude when filtering strictly.
    return restaurants.filter((r) => r.open_now === true);
  }, [restaurants, openNowOnly]);

  const headerCount = useMemo(() => displayedRestaurants.length, [displayedRestaurants]);

  // ====================================================================
  return (
    <div
      data-testid="halal-finder-page"
      className="relative h-screen w-screen overflow-hidden bg-[#FDFBF7] text-[#2A3B2C]"
    >
      {/* ------------------- DESKTOP LAYOUT (split) ------------------- */}
      <div className="hidden lg:grid h-full" style={{ gridTemplateColumns: "420px 1fr" }}>
        <SidebarPanel
          centerLabel={centerLabel}
          headerCount={headerCount}
          loading={loading}
          cuisine={cuisine}
          setCuisine={setCuisine}
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          onSearch={handleSearch}
          onPickSuggestion={handlePickSuggestion}
          userLocation={userLocation}
          onMyLocation={requestGeolocation}
          restaurants={displayedRestaurants}
          openNowOnly={openNowOnly}
          setOpenNowOnly={setOpenNowOnly}
          activeId={activeId}
          onCardClick={handleCardClick}
          onClaim={setClaimRestaurant}
          onReport={setReportRestaurant}
          onRefresh={loadNearby}
          onShare={handleShare}
          onAddRestaurant={() => setAddOpen(true)}
          geoBlocked={geoBlocked}
          cardRefs={cardRefs}
        />

        <div className="relative h-full">
          <MapView
            center={center}
            userLocation={userLocation}
            restaurants={displayedRestaurants}
            activeId={activeId}
            onMarkerClick={handleMarkerClick}
            onBoundsChange={handleBoundsChange}
            fitKey={fitKey}
            initialZoom={zoom}
          />
        </div>
      </div>

      {/* ------------------- MOBILE LAYOUT ------------------- */}
      <div className="lg:hidden h-full flex flex-col">
        {/* Top bar */}
        <header className="relative z-50 px-4 pt-4 pb-3 bg-[#FDFBF7]/95 backdrop-blur-md border-b border-[#E5E1D8]">
          <div className="flex items-center justify-between gap-2">
            <Brand small />
            <div className="flex items-center gap-1.5">
              <Link
                to="/owner"
                data-testid="header-owner-login-mobile"
                aria-label="Restaurant owner login"
                title="Restaurant owners — log in"
                className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white border border-[#E5E1D8] text-[#647466]"
              >
                <Crown className="w-3.5 h-3.5" />
              </Link>
              <button
                type="button"
                data-testid="add-restaurant-btn-mobile"
                onClick={() => setAddOpen(true)}
                title="Submit a new halal restaurant"
                className="inline-flex items-center gap-1 px-2.5 h-8 rounded-full text-xs font-semibold bg-white border border-[#E5E1D8] text-[#2A3B2C]"
              >
                <Plus className="w-3 h-3" />
                Add
              </button>
              <button
                type="button"
                data-testid="share-btn-mobile"
                onClick={handleShare}
                aria-label="Share this view"
                className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white border border-[#E5E1D8] text-[#647466]"
              >
                <Share2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <form onSubmit={handleSearch} className="mt-3 flex items-center gap-2">
            <div className="flex-1">
              <PlaceAutocomplete
                value={searchInput}
                onChange={setSearchInput}
                onPick={handlePickSuggestion}
                onSubmitText={() => handleSearch()}
                userLocation={userLocation}
                placeholder="Try 'biryani', 'shawarma melbourne'..."
                testId="city-search-input-mobile"
                variant="rounded"
              />
            </div>
            <button
              type="button"
              data-testid="my-location-btn-mobile"
              onClick={requestGeolocation}
              className="h-11 w-11 shrink-0 inline-flex items-center justify-center rounded-full bg-white border border-[#E5E1D8] text-[#2A3B2C] hover:border-[#D95D39] hover:text-[#D95D39] transition-colors"
              aria-label="My location"
            >
              <LocateFixed className="w-4 h-4" />
            </button>
          </form>

          <div className="mt-3 flex items-center gap-2 overflow-x-auto no-scrollbar">
            <OpenNowPill
              active={openNowOnly}
              onClick={() => setOpenNowOnly((v) => !v)}
              variant="mobile"
            />
            <CuisinePills cuisine={cuisine} setCuisine={setCuisine} variant="mobile" />
            <span className="ml-auto text-xs text-[#647466] whitespace-nowrap pr-1">
              {loading ? "Searching…" : `${headerCount} found`}
            </span>
          </div>
        </header>

        {/* Map (always present, visible in 'map' view) */}
        <div className={`relative flex-1 ${mobileView === "list" ? "hidden" : "block"}`}>
          <MapView
            center={center}
            userLocation={userLocation}
            restaurants={displayedRestaurants}
            activeId={activeId}
            onMarkerClick={handleMarkerClick}
            onBoundsChange={handleBoundsChange}
            fitKey={fitKey}
          />
        </div>

        {/* List view */}
        <div className={`flex-1 overflow-y-auto brand-scroll p-4 space-y-4 ${
          mobileView === "list" ? "block" : "hidden"
        }`}>
          <FeaturedNearby restaurants={displayedRestaurants} onCardClick={handleCardClick} />
          {displayedRestaurants.length === 0 && !loading && (
            <EmptyState openNowOnly={openNowOnly} onClearOpenNow={() => setOpenNowOnly(false)} />
          )}
          {displayedRestaurants.map((r, i) => (
            <RestaurantCard
              key={r.place_id}
              ref={(el) => (cardRefs.current[r.place_id] = el)}
              restaurant={r}
              active={activeId === r.place_id}
              onClick={() => handleCardClick(r.place_id)}
              onClaim={() => setClaimRestaurant(r)}
              onReport={() => setReportRestaurant(r)}
              index={i}
            />
          ))}
        </div>

        {/* Bottom toggle pill */}
        <div className="absolute left-1/2 -translate-x-1/2 bottom-20 z-40">
          <div
            data-testid="view-toggle"
            className="flex bg-[#2A3B2C] rounded-full p-1 shadow-2xl"
          >
            <ToggleButton
              active={mobileView === "map"}
              onClick={() => setMobileView("map")}
              testId="toggle-map-view"
            >
              <MapIcon className="w-4 h-4 mr-1.5" />
              Map
            </ToggleButton>
            <ToggleButton
              active={mobileView === "list"}
              onClick={() => setMobileView("list")}
              testId="toggle-list-view"
            >
              <List className="w-4 h-4 mr-1.5" />
              List
            </ToggleButton>
          </div>
        </div>
      </div>

      {/* Loading overlay */}
      {loading && !center && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-[#FDFBF7]/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3 text-[#2A3B2C]">
            <Loader2 className="w-7 h-7 animate-spin text-[#D95D39]" />
            <span className="text-sm font-medium">Locating you…</span>
          </div>
        </div>
      )}

      {/* Claim & Sponsor Dialog */}
      <ClaimSponsorDialog
        open={!!claimRestaurant}
        onOpenChange={(o) => !o && setClaimRestaurant(null)}
        restaurant={claimRestaurant}
      />

      {/* Restaurant Details Sheet (tap a card to open) */}
      <RestaurantDetailsSheet
        open={!!detailsRestaurant}
        onOpenChange={(o) => !o && setDetailsRestaurant(null)}
        restaurant={detailsRestaurant}
        onReport={() => detailsRestaurant && setReportRestaurant(detailsRestaurant)}
        onClaim={() => {
          if (!detailsRestaurant) return;
          // Close the sheet first so the claim dialog is unobstructed
          setClaimRestaurant(detailsRestaurant);
          setDetailsRestaurant(null);
        }}
      />

      <AddRestaurantDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        initialCenter={center}
        userLocation={userLocation}
      />

      {/* Report Dialog (community moderation) */}
      <ReportDialog
        open={!!reportRestaurant}
        onOpenChange={(o) => !o && setReportRestaurant(null)}
        restaurant={reportRestaurant}
      />
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function Brand({ small }) {
  return (
    <div className="flex items-center gap-2.5" data-testid="brand-logo">
      <div className="w-9 h-9 rounded-xl bg-[#F5F0E4] border border-[#E5E1D8] flex items-center justify-center shadow-sm overflow-hidden">
        <img
          src="/alkoncierge-logo.png"
          alt="AlKoncierge"
          className="w-7 h-7 object-contain"
          draggable="false"
        />
      </div>
      <div className="leading-tight">
        <div className={`font-display font-semibold ${small ? "text-lg" : "text-xl"} text-[#2A3B2C]`}>
          <span className="text-[#D95D39]">Al</span>Koncierge
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#8A9A8B]">
          Eat halal · anywhere
        </div>
      </div>
    </div>
  );
}

function CuisinePills({ cuisine, setCuisine, variant = "desktop" }) {
  return (
    <>
      {CUISINES.map((c) => (
        <button
          key={c.id}
          data-testid={`cuisine-${c.id}${variant === "mobile" ? "-mobile" : ""}`}
          onClick={() => setCuisine(c.id)}
          className={`shrink-0 h-8 px-3 rounded-full text-xs font-medium border transition-all duration-150 ${
            cuisine === c.id
              ? "bg-[#2A3B2C] text-white border-transparent"
              : "bg-white text-[#647466] border-[#E5E1D8] hover:border-[#2A3B2C] hover:text-[#2A3B2C]"
          }`}
        >
          {c.label}
        </button>
      ))}
    </>
  );
}

function OpenNowPill({ active, onClick, variant = "desktop" }) {
  return (
    <button
      data-testid={`open-now-pill${variant === "mobile" ? "-mobile" : ""}`}
      onClick={onClick}
      title="Show only places open right now"
      className={`shrink-0 inline-flex items-center gap-1 h-8 px-3 rounded-full text-xs font-semibold border transition-all duration-150 ${
        active
          ? "bg-[#2E7D5B] text-white border-transparent shadow-sm"
          : "bg-white text-[#2E7D5B] border-[#2E7D5B]/40 hover:border-[#2E7D5B] hover:bg-[#F0F8F4]"
      }`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${
          active ? "bg-white" : "bg-[#2E7D5B] animate-pulse"
        }`}
      />
      Open now
    </button>
  );
}

function ToggleButton({ active, onClick, children, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`inline-flex items-center px-5 h-10 rounded-full text-sm font-medium transition-colors ${
        active ? "bg-white text-[#2A3B2C]" : "text-white/70 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function EmptyState({ openNowOnly = false, onClearOpenNow }) {
  if (openNowOnly) {
    return (
      <div
        data-testid="empty-state"
        className="text-center py-14 px-6 rounded-2xl border border-dashed border-[#E5E1D8] bg-white"
      >
        <MapPin className="w-10 h-10 mx-auto text-[#D95D39] opacity-60" />
        <p className="mt-4 font-display text-lg text-[#2A3B2C]">
          Nothing open right now
        </p>
        <p className="mt-1 text-sm text-[#647466]">
          Try clearing the <b>Open now</b> filter to see all halal spots in this area.
        </p>
        {onClearOpenNow && (
          <button
            type="button"
            data-testid="clear-open-now"
            onClick={onClearOpenNow}
            className="mt-4 inline-flex items-center justify-center gap-1 h-9 px-4 rounded-full bg-[#2A3B2C] text-white text-xs font-medium hover:bg-[#1f2b21]"
          >
            Show all places
          </button>
        )}
      </div>
    );
  }
  return (
    <div
      data-testid="empty-state"
      className="text-center py-14 px-6 rounded-2xl border border-dashed border-[#E5E1D8] bg-white"
    >
      <Utensils className="w-8 h-8 mx-auto text-[#8A9A8B]" />
      <h3 className="mt-3 font-display font-medium text-lg text-[#2A3B2C]">
        No halal spots in this area
      </h3>
      <p className="mt-1 text-sm text-[#647466]">
        Try increasing the radius or searching a different city.
      </p>
    </div>
  );
}

function SidebarPanel({
  centerLabel,
  headerCount,
  loading,
  cuisine,
  setCuisine,
  searchInput,
  setSearchInput,
  onSearch,
  onPickSuggestion,
  userLocation,
  onMyLocation,
  restaurants,
  openNowOnly,
  setOpenNowOnly,
  activeId,
  onCardClick,
  onClaim,
  onReport,
  onRefresh,
  onShare,
  onAddRestaurant,
  geoBlocked,
  cardRefs,
}) {
  return (
    <aside className="h-full flex flex-col border-r border-[#E5E1D8] bg-[#FDFBF7]">
      <div className="px-6 pt-6 pb-4">
        <div className="flex items-start justify-between gap-3">
          <Brand />
          <div className="flex items-center gap-2">
            <Link
              to="/owner"
              data-testid="header-owner-login"
              className="hidden md:inline-flex items-center gap-1 px-2.5 h-8 rounded-full text-xs font-semibold bg-white border border-[#E5E1D8] text-[#647466] hover:border-[#2A3B2C] hover:text-[#2A3B2C] transition-colors"
              title="Restaurant owners — log in to your dashboard"
            >
              <Crown className="w-3 h-3" />
              Owner login
            </Link>
            <button
              type="button"
              data-testid="add-restaurant-btn"
              onClick={onAddRestaurant}
              title="Submit a new halal restaurant"
              className="inline-flex items-center gap-1 px-2.5 h-8 rounded-full text-xs font-semibold bg-white border border-[#E5E1D8] text-[#2A3B2C] hover:border-[#2A3B2C] transition-colors"
            >
              <Plus className="w-3 h-3" />
              Add
            </button>
            <button
              type="button"
              data-testid="share-btn"
              onClick={onShare}
              title="Share this view"
              className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white border border-[#E5E1D8] text-[#647466] hover:border-[#2A3B2C] hover:text-[#2A3B2C] transition-colors"
            >
              <Share2 className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              data-testid="header-boost-cta"
              onClick={() => {
                if (restaurants.length > 0) {
                  onClaim?.(restaurants[0]);
                }
              }}
              disabled={restaurants.length === 0}
              title="Are you a restaurant owner? Feature your listing."
              className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-semibold bg-gradient-to-r from-[#E1A624] to-[#D95D39] text-white shadow-sm hover:shadow-md hover:scale-[1.03] transition-all disabled:opacity-40"
            >
              <Crown className="w-3 h-3" />
              Boost yours
            </button>
          </div>
        </div>

        <form onSubmit={onSearch} className="mt-5 relative flex items-center gap-2" data-testid="city-search-form">
          <div className="relative flex-1">
            <PlaceAutocomplete
              value={searchInput}
              onChange={setSearchInput}
              onPick={onPickSuggestion}
              onSubmitText={() => onSearch()}
              userLocation={userLocation}
              placeholder="Try 'biryani', 'shawarma melbourne', or a city..."
              testId="city-search-input"
              variant="rounded"
            />
          </div>
          <button
            type="button"
            data-testid="my-location-btn"
            onClick={onMyLocation}
            title="Use my location"
            className="w-12 h-12 shrink-0 inline-flex items-center justify-center rounded-full bg-[#2A3B2C] text-white hover:bg-[#1f2b21] transition-colors"
          >
            <LocateFixed className="w-4 h-4" />
          </button>
        </form>

        {centerLabel && (
          <div className="mt-3 flex items-center gap-1.5 text-xs text-[#647466]">
            <MapPin className="w-3.5 h-3.5 text-[#D95D39]" />
            <span className="truncate">
              {geoBlocked ? "Default · " : ""}
              {centerLabel}
            </span>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D95D39]">
            Cuisine
          </span>
          <button
            data-testid="refresh-btn"
            onClick={onRefresh}
            className="inline-flex items-center gap-1 text-xs text-[#647466] hover:text-[#D95D39] transition-colors"
          >
            <RefreshCcw className="w-3 h-3" />
            Refresh
          </button>
        </div>

        <div
          data-testid="cuisine-pills"
          className="mt-2 -mx-1 px-1 flex items-center gap-2 overflow-x-auto no-scrollbar pb-0.5"
        >
          <OpenNowPill
            active={openNowOnly}
            onClick={() => setOpenNowOnly(!openNowOnly)}
          />
          <CuisinePills cuisine={cuisine} setCuisine={setCuisine} />
        </div>

        <div className="mt-5 flex items-center justify-between">
          <h2 className="font-display text-lg font-medium text-[#2A3B2C]">
            {loading ? "Searching…" : `${headerCount} halal places`}
          </h2>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-[#D95D39]" />}
        </div>
      </div>

      <div
        data-testid="restaurant-list"
        className="flex-1 overflow-y-auto brand-scroll px-6 pb-6 space-y-4"
      >
        <FeaturedNearby restaurants={restaurants} onCardClick={onCardClick} />
        {restaurants.length === 0 && !loading && (
          <EmptyState openNowOnly={openNowOnly} onClearOpenNow={() => setOpenNowOnly(false)} />
        )}
        {restaurants.map((r, i) => (
          <RestaurantCard
            key={r.place_id}
            ref={(el) => (cardRefs.current[r.place_id] = el)}
            restaurant={r}
            active={activeId === r.place_id}
            onClick={() => onCardClick(r.place_id)}
            onClaim={() => onClaim?.(r)}
            onReport={() => onReport?.(r)}
            index={i}
          />
        ))}
      </div>
    </aside>
  );
}
